import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let _cfgCache = null;
let _cfgAt = 0;

async function getCfg() {
  const now = Date.now();
  if (_cfgCache && now - _cfgAt < 60_000) return _cfgCache;
  try {
    const { data } = await axios.get(`${BACKEND_URL}/api/public-config`);
    _cfgCache = data;
    _cfgAt = now;
    return data;
  } catch {
    return { seo: {} };
  }
}

// Static per-route SEO overrides (title + description + keywords). Anything
// not here falls back to admin-configured defaults from /api/public-config.
const ROUTE_SEO = {
  "/": {
    title:
      "CineLUT AI — AI 3D LUT Generator + Creator OS for YouTube & Filmmakers",
    description:
      "Turn any look into your look. Upload footage + a reference frame and get a broadcast-quality cinematic 3D LUT (.cube / .3dl / .look) in seconds. Includes an AI Creator OS with YouTube analytics, thumbnail lab, script lab, and multi-agent growth tools.",
    keywords:
      "AI LUT generator, cinematic LUT, 3D LUT, color grading, .cube file, DaVinci Resolve LUT, Premiere Pro LUT, YouTube AI tools, video color match, Gareebo Ka Filmmaker",
  },
  "/pricing": {
    title: "Pricing — CineLUT AI (₹199/mo Creator plan)",
    description:
      "Simple Creator plan at ₹199/mo — unlimited AI-generated LUTs, full Creator OS access, YouTube analytics, multi-agent AI, thumbnail + script + comment intelligence. GST invoice included.",
    keywords: "cinelut pricing, creator plan, ai lut subscription, gst invoice, video creator tools india",
  },
  "/studio": {
    title: "LUT Studio — Generate a Cinematic LUT from a Reference Frame",
    description:
      "Upload your video and a reference frame. Our AI analyses colour, contrast, mood and skin tone to produce a downloadable .cube / .3dl / .look LUT with 11 colourist sliders and 15 presets.",
    keywords: "lut studio, ai color match, reference frame lut, cinematic color grading",
  },
  "/signup": {
    title: "Sign up — CineLUT AI (Free 2 LUTs / Day)",
    description:
      "Create your free CineLUT AI account and generate 2 cinematic LUTs every day. No credit card required.",
  },
  "/login": {
    title: "Login — CineLUT AI",
    description: "Login to CineLUT AI to access your projects, credits and Creator OS.",
  },
  "/premium": {
    title: "Premium Add-ons — AI Prompts, Presets, Music & Sound FX",
    description:
      "Unlock 6 categories of creator gold: AI prompts, cinematic LUT presets, effect packs, editing templates, royalty-free music and sound FX. Exclusive to Creator plan.",
    keywords: "cinematic presets, ai prompts, sound fx pack, filmmaking templates, royalty free music",
  },
  "/dashboard": {
    title: "My Dashboard — CineLUT AI",
    description: "Your projects, credits, invoices and plan status.",
    robots: "noindex, follow",
  },
  "/invoices": {
    title: "My Invoices — CineLUT AI",
    description: "Download your GST-compliant invoices for CineLUT AI subscriptions.",
    robots: "noindex, follow",
  },
  "/forgot-password": {
    title: "Forgot Password — CineLUT AI",
    description: "Reset your CineLUT AI account password.",
    robots: "noindex, follow",
  },
  "/reset-password": {
    title: "Reset Password — CineLUT AI",
    description: "Choose a new password for your CineLUT AI account.",
    robots: "noindex, follow",
  },
  "/payment/return": {
    title: "Payment Status — CineLUT AI",
    description: "Confirming your CineLUT AI subscription payment.",
    robots: "noindex, follow",
  },
  "/policy/privacy": {
    title: "Privacy Policy — CineLUT AI",
    description: "How Gareebo Ka Filmmaker (CineLUT AI) collects, uses and protects your data.",
  },
  "/policy/terms": {
    title: "Terms of Service — CineLUT AI",
    description: "The terms & conditions that govern your use of CineLUT AI.",
  },
  "/policy/refund": {
    title: "Refund Policy — CineLUT AI",
    description: "Refund policy for CineLUT AI digital subscriptions and credits.",
  },
  "/policy/disclaimer": {
    title: "Disclaimer — CineLUT AI",
    description: "Legal disclaimer for the CineLUT AI website and services.",
  },
  "/policy/shipping": {
    title: "Delivery Policy — CineLUT AI",
    description: "How CineLUT AI delivers digital subscriptions and downloads.",
  },
  "/policy/cancellation": {
    title: "Cancellation Policy — CineLUT AI",
    description: "How to cancel your CineLUT AI subscription.",
  },
  "/blog": {
    title: "Blog — Filmmaking, Color Grading & YouTube Growth Guides",
    description:
      "Deep-dive guides on cinematic filmmaking, LUT design, colour theory, YouTube growth and gear picks from Gareebo Ka Filmmaker.",
    keywords: "filmmaking blog, color grading tutorials, youtube growth tips, camera gear reviews india",
  },
  "/blog/author/neel-varma": {
    title: "Neel Varma — Writer at Gareebo Ka Filmmaker",
    description:
      "Cinematic filmmaking, colour grading and YouTube growth guides by Neel Varma — indie filmmaker and founder of Gareebo Ka Filmmaker.",
    keywords: "neel varma, gareebo ka filmmaker author, indie filmmaker india, cinelut ai founder",
  },
};

function upsertMeta(name, content, attr = "name") {
  if (!content) {
    // Clean up stale meta if content is empty for this key
    const stale = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (stale) stale.setAttribute("content", "");
    return;
  }
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertAltLink(rel, type, title, href) {
  if (!href) return;
  let el = document.head.querySelector(
    `link[rel="${rel}"][type="${type}"]`,
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("type", type);
    document.head.appendChild(el);
  }
  el.setAttribute("title", title);
  el.setAttribute("href", href);
}

// Remove every article:tag meta so route changes don't leak old tags.
function clearArticleTagMetas() {
  document.head.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());
}

export default function SEOHead() {
  const loc = useLocation();
  const [cfg, setCfg] = useState(null);
  const [ovVersion, setOvVersion] = useState(0);

  useEffect(() => { getCfg().then(setCfg); }, []);

  useEffect(() => {
    const bump = () => setOvVersion((v) => v + 1);
    window.addEventListener("seo-override-change", bump);
    return () => window.removeEventListener("seo-override-change", bump);
  }, []);

  useEffect(() => {
    const seo = cfg?.seo || {};
    // If a page has already mounted a dynamic override (e.g. blog post) it
    // will set window.__seoOverride = { title, description, ogImage, ogType,
    // keywords, author, articlePublishedTime, articleModifiedTime, articleTags }
    const dyn = (typeof window !== "undefined" && window.__seoOverride) || {};
    const perRoute = ROUTE_SEO[loc.pathname] || {};
    const title = dyn.title || perRoute.title || seo.default_title || "CineLUT AI";
    const description =
      dyn.description || perRoute.description || seo.default_description || "";
    const keywords = dyn.keywords || perRoute.keywords || "";
    const ogImage = dyn.ogImage || seo.og_image_url || "";
    const ogType = dyn.ogType || "website";
    const canonical = `${window.location.origin}${loc.pathname}`;
    const robotsDirective = perRoute.robots || "index, follow";

    document.title = title;
    upsertMeta("description", description);
    upsertLink("canonical", canonical);
    // RSS feed auto-discovery (browsers + feed readers pick this up)
    upsertAltLink(
      "alternate",
      "application/rss+xml",
      "Gareebo Ka Filmmaker — Blog",
      `${BACKEND_URL || window.location.origin}/api/blog/rss.xml`,
    );
    if (keywords) upsertMeta("keywords", keywords);
    upsertMeta("author", dyn.author || "Gareebo Ka Filmmaker");

    // Open Graph
    upsertMeta("og:site_name", "Gareebo Ka Filmmaker", "property");
    upsertMeta("og:locale", "en_IN", "property");
    upsertMeta("og:title", title, "property");
    upsertMeta("og:description", description, "property");
    upsertMeta("og:type", ogType, "property");
    upsertMeta("og:url", canonical, "property");
    if (ogImage) upsertMeta("og:image", ogImage, "property");

    // Twitter
    upsertMeta("twitter:card", ogImage ? "summary_large_image" : "summary");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    if (ogImage) upsertMeta("twitter:image", ogImage);

    // Article-specific meta (only when og:type=article)
    clearArticleTagMetas();
    if (ogType === "article") {
      if (dyn.articlePublishedTime)
        upsertMeta("article:published_time", dyn.articlePublishedTime, "property");
      if (dyn.articleModifiedTime)
        upsertMeta("article:modified_time", dyn.articleModifiedTime, "property");
      if (dyn.author) upsertMeta("article:author", dyn.author, "property");
      if (Array.isArray(dyn.articleTags)) {
        dyn.articleTags.forEach((tag) => {
          const m = document.createElement("meta");
          m.setAttribute("property", "article:tag");
          m.setAttribute("content", tag);
          document.head.appendChild(m);
        });
      }
    }

    // Search-console verifications
    if (seo.gsc_verification) upsertMeta("google-site-verification", seo.gsc_verification);
    if (seo.bing_verification) upsertMeta("msvalidate.01", seo.bing_verification);

    // robots (per-route override for auth/private pages)
    upsertMeta("robots", robotsDirective);
  }, [cfg, loc.pathname, ovVersion]);

  return null;
}
